import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, addDoc, serverTimestamp, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import {
  getOrCreateDesktop,
  deleteShortcut,
  moveShortcut,
  moveShortcutToFolder,
  renameFolder,
  moveShortcutToDestination,
  updatePinnedApps,
  updateBackground,
  createFolder,
  navigateToFolder,
  deleteFolder,
  createShortcut,
  updateFolderPosition
} from './services/desktopService';
import DesktopShortcut from './components/DesktopShortcut';
import MenuButton from './components/MenuButton';
import Pallet from './components/Pallet';
import BackgroundSelector from './components/BackgroundSelector';
import ShortcutCreator from './components/ShortcutCreator';
import ContextMenu from './components/ContextMenu';
import LoadingSpinner from './components/LoadingSpinner';
import NoteViewer from './NoteViewer';
import EntityViewer from './EntityViewer';
import FloatingWindow from './components/FloatingWindow';
import DiagramViewer from './components/DiagramViewer';
import { useWindowManager } from './hooks/useWindowManager';
import { userCanAccessApp } from './constants/appCatalog';
import './Desktop.css';
import './backgrounds/backgrounds.css';

function Desktop() {
  const navigate = useNavigate();
  
  // Version check
  useEffect(() => {
    console.log('🚀 DESKTOP VERSION: Build28.0-v1.0.4');
  }, []);
  
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Build 25.1: org-wide enabled apps. Same shape and semantics as in Suite.js.
  const [orgSettings, setOrgSettings] = useState(null);
  const [desktop, setDesktop] = useState(null);
  const [draggingShortcut, setDraggingShortcut] = useState(null);
  const [draggingFolder, setDraggingFolder] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const [showShortcutCreator, setShowShortcutCreator] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [shortcutInitialType, setShortcutInitialType] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const [showNoteCreator, setShowNoteCreator] = useState(false);
  const [noteFormData, setNoteFormData] = useState({ title: '', content: '' });

  // Build 28 (item 1): floating window manager replaces the Build 26/27
  // single-overlay state. Multiple notes/diagrams/defects/features can
  // be open at once for side-by-side comparison. The viewers themselves
  // are unchanged — just hosted inside FloatingWindow instances now.
  const wm = useWindowManager();
  const [windowLoading, setWindowLoading] = useState(false);
  // While a shortcut is being dragged, highlight the folder under the cursor
  // so the user has a clear "drop here" affordance.
  const [hoverFolderId, setHoverFolderId] = useState(null);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setAuthLoading(false);
        navigate('/signin');
      }
    });
    return () => unsub();
  }, [navigate]);

  // Build 25.1: org settings listener
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, 'system', 'orgSettings'),
      (snap) => {
        if (snap.exists()) setOrgSettings({ id: snap.id, ...snap.data() });
        else setOrgSettings(null);
      },
      (err) => {
        console.warn('Org settings snapshot failed:', err);
        setOrgSettings(null);
      }
    );
    return () => unsub();
  }, [user]);

  // Set page title
  useEffect(() => {
    document.title = 'Desktop - Wixon & Cross Desk Suite';
  }, []);

  // Desktop listener
  useEffect(() => {
    if (!user) return;

    const loadDesktop = async () => {
      const desktopData = await getOrCreateDesktop(user);
      setDesktop(desktopData);

      // Set up real-time listener
      const unsub = onSnapshot(doc(db, 'desktops', user.uid), (snap) => {
        if (snap.exists()) {
          setDesktop({ id: snap.id, ...snap.data() });
        }
      });

      return () => unsub();
    };

    loadDesktop();
  }, [user]);

  // Listen for unread messages — both DMs and groups.
  // DMs use the legacy `read: false` flag; groups use per-user `lastReadAt`
  // on the conversation document. We combine the two counts.
  useEffect(() => {
    if (!user) return;

    let unreadDms = 0;
    let unreadGroups = 0;

    const dmQuery = query(
      collection(db, 'messages'),
      where('toUserId', '==', user.uid),
      where('read', '==', false)
    );
    const unsubDm = onSnapshot(dmQuery, (snap) => {
      unreadDms = snap.docs.length;
      setUnreadMessageCount(unreadDms + unreadGroups);
    });

    // For group unread we need both the user's conversations (for lastReadAt)
    // and the group messages addressed to them.
    const conversationsState = { byId: {} };
    const groupMessagesState = { docs: [] };

    const recomputeGroups = () => {
      const docs = groupMessagesState.docs;
      let total = 0;
      for (const conv of Object.values(conversationsState.byId)) {
        const lastReadMs = conv.lastReadAt?.[user.uid]?.toMillis?.() ?? 0;
        for (const msg of docs) {
          if (msg.conversationId !== conv.id) continue;
          if (msg.fromUserId === user.uid) continue;
          const created = msg.createdAt?.toMillis?.() ?? 0;
          if (created > lastReadMs) total++;
        }
      }
      unreadGroups = total;
      setUnreadMessageCount(unreadDms + unreadGroups);
    };

    const convQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );
    const unsubConv = onSnapshot(convQuery, (snap) => {
      const byId = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.isGroup) byId[d.id] = { id: d.id, ...data };
      });
      conversationsState.byId = byId;
      recomputeGroups();
    });

    const groupMsgQuery = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      where('isGroup', '==', true)
    );
    const unsubGroupMsg = onSnapshot(groupMsgQuery, (snap) => {
      groupMessagesState.docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      recomputeGroups();
    });

    return () => {
      unsubDm();
      unsubConv();
      unsubGroupMsg();
    };
  }, [user]);

  const handleShortcutOpen = (shortcut) => {
    console.log('Opening shortcut:', shortcut.type, shortcut);
    
    if (shortcut.type === 'folder') {
      navigateToFolder(user.uid, shortcut.id);
    } else if (shortcut.type === 'app') {
      const routes = {
        'defects': '/defect-tracker',
        'features': '/feature-requests',
        'notes': '/notes',
        'messaging': '/messaging',
        'diagramming': '/diagramming',
        'dashboard': '/dashboard',
        'account-requests': '/account-requests',
        'incident-reports': '/incident-reports',
        'injury-reports': '/injury-reports',
        'maintenance-requests': '/maintenance-requests',
        'sop-system': '/sop-system'
      };
      const route = routes[shortcut.appId] || '/dashboard';
      // Open apps in new tab to keep desktop session open
      window.open(route, '_blank');
    } else if (shortcut.type === 'note') {
      // Build 26: open the note inline on the desktop instead of navigating
      // away. When the user closes the viewer they're still on the desktop
      // they came from (item 4 of the spec).
      openNoteInline(shortcut.noteId);
    } else if (shortcut.type === 'defect') {
      // Build 27 (item 7): open defect inline read-only, no navigation.
      openEntityInline('defect', shortcut.defectId);
    } else if (shortcut.type === 'feature') {
      openEntityInline('feature', shortcut.featureId);
    } else if (shortcut.type === 'diagram') {
      // Build 28 (item 1): open diagram read-only in a window.
      openDiagramInline(shortcut.diagramId);
    } else if (shortcut.type === 'url') {
      // Build 26/27/28: if a URL shortcut points at a note/defect/
      // feature/diagram on this site, open it inline. Else new tab.
      const noteId = extractNoteIdFromUrl(shortcut.url);
      const entityRef = extractEntityFromUrl(shortcut.url);
      const diagramId = extractDiagramIdFromUrl(shortcut.url);
      if (noteId) {
        openNoteInline(noteId);
      } else if (entityRef) {
        openEntityInline(entityRef.type, entityRef.id);
      } else if (diagramId) {
        openDiagramInline(diagramId);
      } else {
        window.open(shortcut.url, '_blank');
      }
    }
  };

  // Build 27/28: fetch a defect or feature and open it in a floating
  // window (read-only EntityViewer). dedupeKey prevents a duplicate
  // window — a second click just focuses the existing one.
  const openEntityInline = async (entityType, entityId) => {
    if (!entityId) return;
    const collectionName = entityType === 'feature' ? 'featureRequests' : 'defects';
    setWindowLoading(true);
    try {
      const snap = await getDoc(doc(db, collectionName, entityId));
      if (!snap.exists()) {
        alert(`This ${entityType} no longer exists.`);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      wm.open({
        kind: `entity:${entityType}`,
        payload: { entityType, data },
        title: `${entityType === 'feature' ? '✨' : '🐛'} ${data.title || entityType}`,
        dedupeKey: `${entityType}:${entityId}`
      });
    } catch (err) {
      console.error(`Failed to load ${entityType} for inline view:`, err);
      alert(`Could not load ${entityType}: ${err?.message || 'unknown error'}`);
    } finally {
      setWindowLoading(false);
    }
  };

  // Detect same-origin URLs pointing at /defect-tracker/<id> or
  // /feature-requests/<id> so URL shortcuts from Copy Link open inline.
  const extractEntityFromUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      let m = u.pathname.match(/^\/defect-tracker\/([^/?#]+)$/);
      if (m) return { type: 'defect', id: m[1] };
      m = u.pathname.match(/^\/feature-requests\/([^/?#]+)$/);
      if (m) return { type: 'feature', id: m[1] };
      return null;
    } catch (e) {
      return null;
    }
  };

  // Build 26/28: fetch a note and open it in a floating window.
  const openNoteInline = async (noteId) => {
    if (!noteId) return;
    setWindowLoading(true);
    try {
      const snap = await getDoc(doc(db, 'notes', noteId));
      if (!snap.exists()) {
        alert('This note no longer exists.');
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      wm.open({
        kind: 'note',
        payload: { noteId: data.id, data },
        title: `📝 ${data.title || 'Untitled note'}`,
        dedupeKey: `note:${data.id}`
      });
    } catch (err) {
      console.error('Failed to load note for inline view:', err);
      alert(`Could not load note: ${err?.message || 'unknown error'}`);
    } finally {
      setWindowLoading(false);
    }
  };

  // Detect URLs pointing at /notes/<id> for this origin so URL shortcuts
  // built from "Copy Link" open inline like dedicated note shortcuts do.
  const extractNoteIdFromUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      const m = u.pathname.match(/^\/notes\/([^/?#]+)$/);
      if (m) return m[1];
      const q = u.searchParams.get('noteId');
      return q || null;
    } catch (e) {
      return null;
    }
  };

  // Build 28 (item 1): fetch a diagram and open it read-only in a
  // floating window (DiagramViewer). Same dedupe pattern as the others.
  const openDiagramInline = async (diagramId) => {
    if (!diagramId) return;
    setWindowLoading(true);
    try {
      const snap = await getDoc(doc(db, 'diagrams', diagramId));
      if (!snap.exists()) {
        alert('This diagram no longer exists.');
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      wm.open({
        kind: 'diagram',
        payload: { data },
        title: `${data.mode === 'flowchart' ? '📊' : '✏️'} ${data.title || 'Untitled diagram'}`,
        dedupeKey: `diagram:${data.id}`,
        size: { w: 880, h: 620 }
      });
    } catch (err) {
      console.error('Failed to load diagram for inline view:', err);
      alert(`Could not load diagram: ${err?.message || 'unknown error'}`);
    } finally {
      setWindowLoading(false);
    }
  };

  // Same-origin /diagramming?id= or /diagrams/<id> detection.
  const extractDiagramIdFromUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      const m = u.pathname.match(/^\/diagrams\/([^/?#]+)$/);
      if (m) return m[1];
      const q = u.searchParams.get('diagramId') || u.searchParams.get('id');
      if (q && u.pathname.startsWith('/diagramming')) return q;
      return null;
    } catch (e) {
      return null;
    }
  };

  // Build 28: NoteViewer save handler, now per-window. Each note window
  // passes its own note id; we update Firestore and refresh that
  // window's payload so it reflects the save.
  const handleSaveWindowNote = async (winId, noteId, updates) => {
    if (!noteId) return;
    await updateDoc(doc(db, 'notes', noteId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    const current = wm.windows.find(w => w.id === winId);
    wm.setGeometry(winId, {
      payload: {
        noteId,
        data: { ...((current && current.payload && current.payload.data) || {}), ...updates }
      }
    });
  };

  const handleAddAppShortcut = async (title, icon, appId) => {
    try {
      await createShortcut(user.uid, {
        title,
        icon,
        appId,
        type: 'app',
        position: clickPosition || { x: 50, y: 50 },
        folderId: desktop.currentFolder
      });
    } catch (err) {
      console.error('Error creating app shortcut:', err);
      alert('Error creating app shortcut');
    }
  };

  const handleShortcutEdit = (shortcut) => {
    setEditingShortcut(shortcut);
    setShowShortcutCreator(true);
  };

  // Build 27 (item 3): folder rename. The folder right-click "Edit"
  // previously called an empty no-op. Now it prompts for a new name
  // and persists via the renameFolder service function.
  const handleFolderEdit = async (folder) => {
    const newName = prompt('Rename folder:', folder.name);
    if (newName === null) return; // user cancelled
    const trimmed = newName.trim();
    if (!trimmed) {
      alert('Folder name cannot be empty.');
      return;
    }
    if (trimmed === folder.name) return; // no change
    try {
      await renameFolder(user.uid, folder.id, trimmed);
    } catch (err) {
      console.error('Error renaming folder:', err);
      alert(`Could not rename folder: ${err?.message || 'unknown error'}`);
    }
  };

  const handleShortcutDelete = async (shortcutId) => {
    try {
      await deleteShortcut(user.uid, shortcutId);
    } catch (err) {
      console.error('Error deleting shortcut:', err);
      alert('Error deleting shortcut');
    }
  };

  // Build 27 (item 5): move a desktop shortcut to another folder (or to
  // the desktop root). targetFolderId === null means "out to the root".
  const handleShortcutMove = async (shortcut, targetFolderId) => {
    try {
      await moveShortcutToDestination(user.uid, shortcut.id, targetFolderId);
    } catch (err) {
      console.error('Error moving shortcut:', err);
      alert(`Could not move shortcut: ${err?.message || 'unknown error'}`);
    }
  };

  const handleDragStart = (shortcutId, e) => {
    const shortcut = desktop.shortcuts.find(s => s.id === shortcutId);
    if (shortcut) {
      setDraggingShortcut(shortcutId);
      setDragOffset({
        x: e.clientX - shortcut.position.x,
        y: e.clientY - shortcut.position.y
      });
    }
  };

  const handleDragEnd = async () => {
    setDraggingShortcut(null);
  };

  // Build 26: detect whether a drop position overlaps a folder in the
  // current scope. Returns the folder id, or null if no overlap.
  //
  // Each shortcut/folder icon is rendered at 80px wide; the title text
  // below adds visible height. We use an 80x80 hit box centered on the
  // icon position — small enough to feel deliberate (you have to drop on
  // the folder, not near it), large enough to be forgiving.
  //
  // Folders outside the current folder scope are ignored — you can only
  // drop into folders you can currently see.
  const findFolderAtPosition = (position) => {
    const ICON_SIZE = 80;
    const folders = (desktop?.folders || []).filter(
      f => f.parentFolder === desktop.currentFolder
    );
    for (const f of folders) {
      const fx = f.position?.x || 0;
      const fy = f.position?.y || 0;
      if (
        position.x >= fx &&
        position.x <= fx + ICON_SIZE &&
        position.y >= fy &&
        position.y <= fy + ICON_SIZE
      ) {
        return f.id;
      }
    }
    return null;
  };

  const handleMouseMove = (e) => {
    if (draggingShortcut) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const newPos = { x: Math.max(0, newX), y: Math.max(0, newY) };

      // Build 26: highlight folder under the cursor so the user has a
      // clear "drop here" affordance.
      setHoverFolderId(findFolderAtPosition(newPos));

      // Update position in state immediately for smooth dragging
      setDesktop(prev => ({
        ...prev,
        shortcuts: prev.shortcuts.map(s =>
          s.id === draggingShortcut
            ? { ...s, position: newPos }
            : s
        )
      }));
    } else if (draggingFolder) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Update folder position in state immediately for smooth dragging
      setDesktop(prev => ({
        ...prev,
        folders: prev.folders.map(f =>
          f.id === draggingFolder
            ? { ...f, position: { x: Math.max(0, newX), y: Math.max(0, newY) } }
            : f
        )
      }));
    }
  };

  const handleMouseUp = async () => {
    if (draggingShortcut) {
      const shortcut = desktop.shortcuts.find(s => s.id === draggingShortcut);
      if (shortcut) {
        // Build 26: if the drop point overlaps a folder, move the shortcut
        // INTO that folder rather than just updating its xy position.
        // This fixes the bug where dropping a shortcut on a folder
        // visually covered the folder without moving the shortcut into it.
        const targetFolderId = findFolderAtPosition(shortcut.position);
        try {
          if (targetFolderId && targetFolderId !== shortcut.folderId) {
            await moveShortcutToFolder(user.uid, draggingShortcut, targetFolderId);
          } else {
            await moveShortcut(user.uid, draggingShortcut, shortcut.position);
          }
        } catch (err) {
          console.error('Error moving shortcut:', err);
        }
      }
      setDraggingShortcut(null);
      setHoverFolderId(null);
    } else if (draggingFolder) {
      const folder = desktop.folders.find(f => f.id === draggingFolder);
      if (folder) {
        try {
          await updateFolderPosition(user.uid, draggingFolder, folder.position);
        } catch (err) {
          console.error('Error moving folder:', err);
        }
      }
      setDraggingFolder(null);
    }
  };

  const handleFolderDragStart = (folderId, e) => {
    const folder = desktop.folders.find(f => f.id === folderId);
    if (folder) {
      setDraggingFolder(folderId);
      setDragOffset({
        x: e.clientX - folder.position.x,
        y: e.clientY - folder.position.y
      });
    }
  };

  const handleUnpinApp = async (appId) => {
    try {
      const updatedPinnedApps = desktop.pinnedApps.filter(app => app.appId !== appId);
      await updatePinnedApps(user.uid, updatedPinnedApps);
    } catch (err) {
      console.error('Error unpinning app:', err);
      alert('Error unpinning app');
    }
  };

  const handleBackgroundChange = async (backgroundId) => {
    try {
      await updateBackground(user.uid, backgroundId);
      setShowBackgroundSelector(false);
    } catch (err) {
      console.error('Error changing background:', err);
      alert('Error changing background');
    }
  };

  const handleCreateFolder = async () => {
    const folderName = prompt('Enter folder name:');
    if (!folderName) return;
    
    try {
      await createFolder(user.uid, folderName, { x: 50, y: 150 });
    } catch (err) {
      console.error('Error creating folder:', err);
      alert('Error creating folder');
    }
  };

  const handleGoBack = async () => {
    const currentFolder = desktop.folders?.find(f => f.id === desktop.currentFolder);
    await navigateToFolder(user.uid, currentFolder?.parentFolder || null);
  };

  // Build breadcrumb path
  const getBreadcrumbs = () => {
    if (!desktop.currentFolder) return [];
    
    const breadcrumbs = [];
    let folderId = desktop.currentFolder;
    
    while (folderId) {
      const folder = desktop.folders?.find(f => f.id === folderId);
      if (!folder) break;
      breadcrumbs.unshift(folder);
      folderId = folder.parentFolder;
    }
    
    return breadcrumbs;
  };

  const handleDesktopContextMenu = (e) => {
    // Right-click on empty desktop space
    const isShortcut = e.target.closest('.desktop-shortcut');
    if (!isShortcut) {
      e.preventDefault();
      const x = e.clientX;
      const y = e.clientY;
      setClickPosition({ x, y });
      setContextMenu({ visible: true, x, y });
    }
  };

  const handleCreateNote = async () => {
    setShowNoteCreator(true);
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!noteFormData.title.trim()) {
      alert('Please enter a note title');
      return;
    }

    try {
      // Create note in Firestore
      const noteRef = await addDoc(collection(db, 'notes'), {
        title: noteFormData.title,
        content: noteFormData.content,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.uid,
        createdByEmail: user.email,
        sharedWith: [],
        category: 'Quick Note'
      });

      // Create desktop shortcut at click position
      await createShortcut(user.uid, {
        type: 'note',
        noteId: noteRef.id,
        title: noteFormData.title,
        icon: '📝',
        position: clickPosition,
        folderId: desktop.currentFolder
      });

      setNoteFormData({ title: '', content: '' });
      setShowNoteCreator(false);
      alert('Note created!');
    } catch (err) {
      console.error('Error creating note:', err);
      alert(`Error creating note: ${err.message}`);
    }
  };

  const handleArrangeIcons = async () => {
    try {
      const ICON_WIDTH = 100;
      const ICON_HEIGHT = 120;
      const PADDING = 20;
      const MARGIN = 50;

      // Build 27 (item 4): when inside a folder the breadcrumb nav bar is
      // fixed at the top of the desktop (top: 20px + padding + content,
      // roughly 70px tall). Arranging icons at the normal top margin put
      // the first row underneath it. Push the starting Y below the
      // breadcrumb when it's visible so arranged icons never hide behind it.
      const BREADCRUMB_RESERVED = 90; // breadcrumb height + a little gap
      const topOffset = desktop.currentFolder ? BREADCRUMB_RESERVED : MARGIN;

      const availableWidth = window.innerWidth - (MARGIN * 2);
      const iconsPerRow = Math.floor(availableWidth / (ICON_WIDTH + PADDING));
      
      // Get all items in current location
      const currentFolders = (desktop.folders || []).filter(
        f => f.parentFolder === desktop.currentFolder
      );
      const currentShortcuts = desktop.shortcuts.filter(
        s => (s.folderId || null) === desktop.currentFolder
      );
      
      const allItems = [...currentFolders, ...currentShortcuts];
      
      // Calculate new positions
      const updates = allItems.map((item, index) => {
        const row = Math.floor(index / iconsPerRow);
        const col = index % iconsPerRow;
        
        return {
          ...item,
          position: {
            x: MARGIN + (col * (ICON_WIDTH + PADDING)),
            y: topOffset + (row * (ICON_HEIGHT + PADDING))
          }
        };
      });
      
      // Update folders
      const updatedFolders = desktop.folders.map(f => {
        const update = updates.find(u => u.id === f.id && 'parentFolder' in u);
        return update || f;
      });
      
      // Update shortcuts
      const updatedShortcuts = desktop.shortcuts.map(s => {
        const update = updates.find(u => u.id === s.id && !('parentFolder' in u));
        return update || s;
      });
      
      // Save to Firestore
      const desktopRef = doc(db, 'desktops', user.uid);
      await updateDoc(desktopRef, {
        folders: updatedFolders,
        shortcuts: updatedShortcuts,
        updatedAt: serverTimestamp()
      });
      
    } catch (err) {
      console.error('Error arranging icons:', err);
      alert('Error arranging icons');
    }
  };

  // Build 25: legacy menu IDs → catalog app IDs.
  // The Desktop's menus use a couple of short-form IDs ('defects', 'features')
  // that don't match the suite-app catalog IDs ('defect-tracker',
  // 'feature-requests'). Map them so we can filter the submenus by per-user
  // access. Items not in the map (like 'dashboard') aren't real apps and
  // always pass through.
  const menuIdToCatalogId = {
    'defects': 'defect-tracker',
    'features': 'feature-requests',
    'add-defects': 'defect-tracker',
    'add-features': 'feature-requests',
    'add-notes': 'notes',
    'add-messaging': 'messaging',
    'add-diagramming': 'diagramming',
    'add-account-requests': 'account-requests',
    'add-incident-reports': 'incident-reports',
    'add-injury-reports': 'injury-reports',
    'add-sop-system': 'sop-system',
    'add-maintenance-requests': 'maintenance-requests'
    // Note: 'add-dashboard' intentionally omitted — Dashboard isn't a
    // catalog app, it's the launcher; always allowed.
  };

  const canAccessMenuItem = (menuItemId) => {
    // 'notes' / 'messaging' / etc menu IDs match catalog IDs as-is
    const catalogId = menuIdToCatalogId[menuItemId] || menuItemId;
    // If it's not a catalog app at all (e.g. 'add-dashboard'), let it through.
    const ALL_CATALOG_IDS = ['defect-tracker','feature-requests','notes','messaging',
      'account-requests','diagramming','incident-reports','injury-reports',
      'sop-system','maintenance-requests'];
    if (!ALL_CATALOG_IDS.includes(catalogId)) return true;
    return userCanAccessApp(userProfile, catalogId, orgSettings);
  };

  const getContextMenuItems = () => {
    const allItems = [
      {
        id: 'apps',
        label: 'Application Links',
        icon: '🔗',
        submenu: [
          { id: 'defects', label: 'Defect Tracker', icon: '🐛', onClick: () => navigate('/defect-tracker') },
          { id: 'features', label: 'Feature Requests', icon: '💡', onClick: () => navigate('/feature-requests') },
          { id: 'notes', label: 'Notes', icon: '📝', onClick: () => navigate('/notes') },
          { id: 'messaging', label: 'Messaging', icon: '💬', onClick: () => navigate('/messaging') },
          { id: 'diagramming', label: 'Diagramming', icon: '📐', onClick: () => navigate('/diagramming') },
          { id: 'account-requests', label: 'Account Requests', icon: '📋', onClick: () => navigate('/account-requests') },
          { id: 'incident-reports', label: 'Incident Reports', icon: '🚨', onClick: () => navigate('/incident-reports') },
          { id: 'injury-reports', label: 'Injury Reporting', icon: '🩹', onClick: () => navigate('/injury-reports') },
          { id: 'sop-system', label: 'SOP System', icon: '📋', onClick: () => navigate('/sop-system') },
          { id: 'maintenance-requests', label: 'Maintenance Requests', icon: '🔧', onClick: () => navigate('/maintenance-requests') }
        ].filter(item => canAccessMenuItem(item.id))
      },
      {
        id: 'new-app-shortcut',
        label: 'Add App Shortcut',
        icon: '➕',
        submenu: [
          { 
            id: 'add-defects', 
            label: 'Defect Tracker', 
            icon: '🐛', 
            onClick: () => handleAddAppShortcut('Defect Tracker', '🐛', 'defects')
          },
          { 
            id: 'add-features', 
            label: 'Feature Requests', 
            icon: '💡', 
            onClick: () => handleAddAppShortcut('Feature Requests', '💡', 'features')
          },
          { 
            id: 'add-notes', 
            label: 'Notes', 
            icon: '📝', 
            onClick: () => handleAddAppShortcut('Notes', '📝', 'notes')
          },
          { 
            id: 'add-messaging', 
            label: 'Messaging', 
            icon: '💬', 
            onClick: () => handleAddAppShortcut('Messaging', '💬', 'messaging')
          },
          { 
            id: 'add-diagramming', 
            label: 'Diagramming', 
            icon: '📐', 
            onClick: () => handleAddAppShortcut('Diagramming', '📐', 'diagramming')
          },
          { 
            id: 'add-dashboard', 
            label: 'Dashboard', 
            icon: '📦', 
            onClick: () => handleAddAppShortcut('Dashboard', '📦', 'dashboard')
          },
          { 
            id: 'add-account-requests', 
            label: 'Account Requests', 
            icon: '📋', 
            onClick: () => handleAddAppShortcut('Account Requests', '📋', 'account-requests')
          },
          { 
            id: 'add-incident-reports', 
            label: 'Incident Reports', 
            icon: '🚨', 
            onClick: () => handleAddAppShortcut('Incident Reports', '🚨', 'incident-reports')
          },
          { 
            id: 'add-injury-reports', 
            label: 'Injury Reporting', 
            icon: '🩹', 
            onClick: () => handleAddAppShortcut('Injury Reporting', '🩹', 'injury-reports')
          },
          { 
            id: 'add-sop-system', 
            label: 'SOP System', 
            icon: '📋', 
            onClick: () => handleAddAppShortcut('SOP System', '📋', 'sop-system')
          },
          { 
            id: 'add-maintenance-requests', 
            label: 'Maintenance Requests', 
            icon: '🔧', 
            onClick: () => handleAddAppShortcut('Maintenance Requests', '🔧', 'maintenance-requests')
          }
        ].filter(item => canAccessMenuItem(item.id))
      },
      {
        id: 'notes-menu',
        label: 'Notes',
        icon: '📝',
        submenu: [
          { id: 'new-note', label: 'New Note', icon: '➕', onClick: handleCreateNote },
          { id: 'link-note', label: 'Link to Existing Note', icon: '🔗', onClick: () => setShowShortcutCreator(true) }
        ]
      },
      {
        id: 'new-folder',
        label: 'New Folder',
        icon: '📁',
        onClick: async () => {
          const name = prompt('Enter folder name:');
          if (name) {
            await createFolder(user.uid, name, clickPosition, desktop.currentFolder);
          }
        }
      },
      {
        id: 'new-shortcut',
        label: 'New Web Shortcut',
        icon: '🌐',
        onClick: () => {
          setShortcutInitialType('url');
          setShowShortcutCreator(true);
        }
      },
      {
        id: 'arrange-icons',
        label: 'Arrange Icons',
        icon: '📐',
        onClick: handleArrangeIcons
      }
    ];
    return allItems;
  };

  if (authLoading || !desktop) return <LoadingSpinner message="Loading desktop..." />;

  return (
    <div
      className={`desktop desktop-background-${desktop.background}`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onContextMenu={handleDesktopContextMenu}
    >
      {/* Background selector button */}
      <button
        className="background-selector-button"
        onClick={() => setShowBackgroundSelector(true)}
        title="Change background"
      >
        🎨
      </button>

      {/* User info */}
      <div className="desktop-user-info">
        <span style={{color: '#1f2937', fontWeight: '500'}}>{user.email}</span>
        <span className="desktop-user-role">{userProfile?.role}</span>
        <button 
          className="btn btn-signout" 
          onClick={async () => {
            if (window.confirm('Sign out?')) {
              await auth.signOut();
              navigate('/signin');
            }
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Breadcrumb navigation - only show when inside a folder */}
      {desktop.currentFolder && (
        <div className="breadcrumb-nav">
          <button 
            className="breadcrumb-item breadcrumb-home"
            onClick={() => navigateToFolder(user.uid, null)}
          >
            🏠 Desktop
          </button>
          {getBreadcrumbs().map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span className="breadcrumb-separator">›</span>
              <button 
                className="breadcrumb-item"
                onClick={() => navigateToFolder(user.uid, folder.id)}
              >
                {folder.icon} {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Shortcuts */}
      <div className="desktop-shortcuts">
        {/* Show folders in current folder */}
        {(desktop.folders || [])
          .filter(folder => folder.parentFolder === desktop.currentFolder)
          .map(folder => (
            <DesktopShortcut
              key={folder.id}
              shortcut={{...folder, type: 'folder', title: folder.name}}
              onOpen={handleShortcutOpen}
              onEdit={() => handleFolderEdit(folder)}
              onDelete={async () => {
                if (window.confirm(`Delete folder "${folder.name}"?\n\nContents will be moved to parent folder.`)) {
                  await deleteFolder(user.uid, folder.id);
                }
              }}
              isDragging={draggingFolder === folder.id}
              isDropTarget={hoverFolderId === folder.id}
              onDragStart={(id, e) => handleFolderDragStart(id, e)}
              onDragEnd={handleDragEnd}
              unreadCount={0}
            />
          ))}
        
        {/* Show shortcuts in current folder */}
        {desktop.shortcuts
          .filter(shortcut => (shortcut.folderId || null) === desktop.currentFolder)
          .filter(shortcut => {
            // Build 25: hide app shortcuts the user no longer has access to.
            // Non-app shortcuts (URLs, notes) pass through unconditionally.
            if (shortcut.type !== 'app') return true;
            return canAccessMenuItem(shortcut.appId);
          })
          .map(shortcut => (
            <DesktopShortcut
              key={shortcut.id}
              shortcut={shortcut}
              onOpen={handleShortcutOpen}
              onEdit={handleShortcutEdit}
              onDelete={handleShortcutDelete}
              onMove={handleShortcutMove}
              moveTargets={(desktop.folders || [])
                .map(f => ({ id: f.id, name: f.name }))}
              isDragging={draggingShortcut === shortcut.id}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              unreadCount={shortcut.appId === 'messaging' ? unreadMessageCount : 0}
            />
          ))}
      </div>

      {/* Menu Button */}
      <MenuButton />

      {/* Pallet (Taskbar) */}
      <Pallet 
        pinnedApps={(desktop.pinnedApps || []).filter(p =>
          // p.appId is the legacy menu id ('defects', 'features', etc) for
          // built-in pinned apps. Translate via the same helper so the
          // filter behaves identically to the menus above.
          canAccessMenuItem(p.appId)
        )} 
        onUnpin={handleUnpinApp} 
        unreadCount={unreadMessageCount}
      />

      {/* Background Selector Modal */}
      {showBackgroundSelector && (
        <BackgroundSelector
          currentBackground={desktop.background}
          onSelect={handleBackgroundChange}
          onClose={() => setShowBackgroundSelector(false)}
        />
      )}

      {/* Shortcut Creator Modal */}
      {showShortcutCreator && (
        <ShortcutCreator
          userId={user.uid}
          currentFolder={desktop.currentFolder}
          position={clickPosition}
          initialType={shortcutInitialType}
          editingShortcut={editingShortcut}
          onClose={() => {
            setShowShortcutCreator(false);
            setShortcutInitialType(null);
            setEditingShortcut(null);
          }}
        />
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={() => setContextMenu({ visible: false, x: 0, y: 0 })}
        />
      )}

      {/* Note Creator Modal */}
      {showNoteCreator && (
        <div className="modal-overlay" onClick={() => setShowNoteCreator(false)}>
          <div className="modal-content note-creator-modal" onClick={e => e.stopPropagation()}>
            <h2>Create New Note</h2>
            <form onSubmit={handleNoteSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={noteFormData.title}
                  onChange={e => setNoteFormData({...noteFormData, title: e.target.value})}
                  placeholder="Note title..."
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={noteFormData.content}
                  onChange={e => setNoteFormData({...noteFormData, content: e.target.value})}
                  placeholder="Note content..."
                  rows="8"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Create Note</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNoteCreator(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Build 28 (item 1): floating windows. Each open note/diagram/
          defect/feature is a FloatingWindow hosting the existing viewer
          in "embedded" mode (fills the window body, no screen dimming). */}
      {windowLoading && (
        <div className="note-viewer-overlay">
          <LoadingSpinner message="Loading..." />
        </div>
      )}

      {/* v1.0.3 fix: skip minimized windows here — they're represented
          by chips in the taskbar below. Previously minimize set the
          flag but didn't hide the window, so click had no visible
          effect. */}
      {wm.windows.filter(w => !w.minimized).map(win => (
        <FloatingWindow
          key={win.id}
          win={win}
          onFocus={wm.focus}
          onClose={wm.close}
          onMinimize={wm.minimize}
          onGeometry={wm.setGeometry}
        >
          {win.kind === 'note' && (
            <NoteViewer
              note={win.payload.data}
              mode="edit"
              embedded
              currentUserId={user?.uid}
              showPinToDesktop={false}
              onClose={() => wm.close(win.id)}
              onSave={(updates) => handleSaveWindowNote(win.id, win.payload.noteId, updates)}
            />
          )}
          {win.kind && win.kind.startsWith('entity:') && (
            <EntityViewer
              entity={win.payload.data}
              entityType={win.payload.entityType}
              embedded
              currentUserId={user?.uid}
              showPinToDesktop={false}
              onClose={() => wm.close(win.id)}
              onOpenFullEditor={() => {
                const prefix = win.payload.entityType === 'feature'
                  ? '/feature-requests' : '/defect-tracker';
                window.open(`${prefix}/${win.payload.data.id}`, '_blank');
              }}
            />
          )}
          {win.kind === 'diagram' && (
            <DiagramViewer diagram={win.payload.data} />
          )}
        </FloatingWindow>
      ))}

      {/* Minimized-window taskbar */}
      {wm.windows.some(w => w.minimized) && (
        <div className="fw-taskbar">
          {wm.windows.filter(w => w.minimized).map(w => (
            <button
              key={w.id}
              className="fw-taskbar-item"
              onClick={() => wm.restore(w.id)}
              title={w.title}
            >
              {w.title}
            </button>
          ))}
        </div>
      )}

      {/* "Tile all" — only when 2+ windows are open (the compare case) */}
      {wm.windows.filter(w => !w.minimized).length >= 2 && (
        <button className="fw-tile-btn" onClick={wm.tileAll} title="Arrange all windows side by side">
          ▦ Tile all
        </button>
      )}
    </div>
  );
}

export default Desktop;
